import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import Button from "@material-ui/core/Button";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import InputAdornment from "@material-ui/core/InputAdornment";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TablePagination from "@material-ui/core/TablePagination";
import TextField from "@material-ui/core/TextField";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Box from "@material-ui/core/Box";
import { useTheme } from "@material-ui/core/styles";
import { useStyles } from "./campaignsStyles";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";

import { Grid } from "@material-ui/core";
import CampaignModal from "../../components/CampaignModal";
import CampaignExecutionsModal from "../../components/CampaignExecutionsModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import { useTimezone } from "../../hooks/useTimezone";
import { i18n } from "../../translate/i18n";
import moment from "moment-timezone";
import CampaignActionModals from "./components/CampaignActionModals";
import CampaignTableRow from "./components/CampaignTableRow";
import { canOpenCampaignModal } from "./utils/campaignRules";
import useCampaignsPage from "./hooks/useCampaignsPage";

const Campaigns = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { formatDateTime, formatHoraDePared, parseHoraDePared, getConfiguredTimezone } = useTimezone();

  // El rodeo local desaparece: `formatHoraDePared` vive en el hook y lo comparten todas
  // las pantallas. Esta funcion se habia copiado ya en dos sitios distintos — el sintoma
  // de que faltaba la distincion entre hora de pared e instante. Ver doc 0412 §11.
  const formatCampaignScheduledDate = formatHoraDePared;

  const {
    campaigns,
    loading,
    searchParam,
    setSearchParam,
    deletingCampaign,
    setDeletingCampaign,
    pauseModalOpen,
    setPauseModalOpen,
    campaignToPause,
    setCampaignToPause,
    stopModalOpen,
    setStopModalOpen,
    campaignToStop,
    setCampaignToStop,
    fetchCampaigns,
    handleDeleteCampaign,
    handlePauseCampaign,
    confirmPauseCampaign,
    handleStopCampaign,
    confirmStopCampaign,
    restartCampaign,
    copyCampaign,
  } = useCampaignsPage();

  // ✅ Estados para paginación frontend
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(13);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [executionsModalOpen, setExecutionsModalOpen] = useState(false);
  const [selectedCampaignForExecutions, setSelectedCampaignForExecutions] = useState(null);
  const [selectedCampaignType, setSelectedCampaignType] = useState(null); // 'UNICA' o 'RECURRENTE'
  const [addMenuAnchorEl, setAddMenuAnchorEl] = useState(null);
  const [initialCampaignValues, setInitialCampaignValues] = useState(null);

  // Resetear paginación frontend al cambiar búsqueda
  useEffect(() => {
    setPage(0);
  }, [searchParam]);

  // ✅ Resetear paginación frontend cuando se cargan nuevas campañas (sin búsqueda)
  useEffect(() => {
    if (campaigns.length > 0 && !searchParam) {
      setPage(0);
    }
  }, [campaigns.length, searchParam]);

  const handleOpenCampaignModal = (campaignType = 'UNICA') => {
    setSelectedCampaign(null);
    setSelectedCampaignType(campaignType);
    setCampaignModalOpen(true);
  };

  // ✅ Nueva función para manejar el dropdown del botón principal
  const handleOpenAddMenu = (event) => {
    event.stopPropagation();
    setAddMenuAnchorEl(event.currentTarget);
  };

  const handleCloseAddMenu = () => {
    setAddMenuAnchorEl(null);
  };

  const handleCloseCampaignModal = () => {
    setSelectedCampaign(null);
    setInitialCampaignValues(null);
    setCampaignModalOpen(false);
  };

  const handleCopyCampaign = (campaign) => {
    copyCampaign(campaign, (copiedValues) => {
      setInitialCampaignValues(copiedValues);
      setSelectedCampaign(null);
      setSelectedCampaignType(copiedValues.tipo);
      setCampaignModalOpen(true);
    });
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditCampaign = (campaign) => {
    // ✅ Validar si se puede abrir el modal (editable o solo lectura)
    if (!canOpenCampaignModal(campaign)) {
      let errorMessage = "";

      if (campaign.status === "EM_ANDAMENTO") {
        errorMessage = "No se puede editar una campaña en proceso. Debe pausarla o pararla primero.";
      } else if (campaign.status === "FINALIZADA" && campaign.isRecurring) {
        errorMessage = "No se puede editar una campaña recurrente finalizada.";
      } else if (campaign.status === "PROGRAMADA") {
        // HORA DE PARED: con `moment()` a secas la regla de «faltan menos de 10 minutos»
        // se evaluaba con el reloj del NAVEGADOR. Ver doc 0412 §11.
        const timeToCheck = campaign.isRecurring && campaign.executionCount > 0 && campaign.nextScheduledAt
          ? parseHoraDePared(campaign.nextScheduledAt)
          : parseHoraDePared(campaign.scheduledAt);

        if (timeToCheck && timeToCheck.isValid()) {
          const minutesUntilExecution = timeToCheck.diff(moment(), "minutes");
          if (minutesUntilExecution <= 10) {
            errorMessage = "No se puede editar una campaña con menos de 10 minutos para su ejecución.";
          }
        } else {
          errorMessage = "No se puede editar esta campaña. Fecha de ejecución inválida.";
        }
      } else {
        errorMessage = "No se puede editar esta campaña en su estado actual.";
      }

      if (errorMessage) {
        toast.error(errorMessage);
        return;
      }
    }

    // Abrir el modal (editable o solo lectura)
    setSelectedCampaign(campaign);
    setCampaignModalOpen(true);
  };

  const handleDeleteCampaignClick = (campaign) => {
    setConfirmModalOpen(true);
    setDeletingCampaign(campaign);
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingCampaign &&
          `${i18n.t("campaigns.confirmationModal.deleteTitle")} ${
            deletingCampaign.name
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteCampaign(deletingCampaign.id)}
      >
        {i18n.t("campaigns.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <CampaignActionModals
        pauseModalOpen={pauseModalOpen}
        campaignToPause={campaignToPause}
        confirmPauseCampaign={confirmPauseCampaign}
        setPauseModalOpen={setPauseModalOpen}
        setCampaignToPause={setCampaignToPause}
        stopModalOpen={stopModalOpen}
        campaignToStop={campaignToStop}
        confirmStopCampaign={confirmStopCampaign}
        setStopModalOpen={setStopModalOpen}
        setCampaignToStop={setCampaignToStop}
      />

      <CampaignModal
        resetPagination={fetchCampaigns}
        open={campaignModalOpen}
        onClose={handleCloseCampaignModal}
        aria-labelledby="form-dialog-title"
        campaignId={selectedCampaign && selectedCampaign.id}
        campaignType={selectedCampaignType || (selectedCampaign && selectedCampaign.tipo) || 'UNICA'}
        initialValues={initialCampaignValues}
      />
      <MainHeader>
        <Grid style={{ width: "100%" }} container>
          <Grid xs={12} sm={8} item>
            <Title>{i18n.t("campaigns.title")}</Title>
          </Grid>
          <Grid xs={12} sm={4} item>
            <Grid spacing={2} container>
              <Grid xs={6} sm={6} item>
                <TextField
                  fullWidth
                  placeholder={i18n.t("campaigns.searchPlaceholder")}
                  type="search"
                  value={searchParam}
                  onChange={handleSearch}
                  className={classes.searchField}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ color: theme.palette.primary.main }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid xs={6} sm={6} item>
                <Box
                  position="relative"
                  display="inline-flex"
                  style={{
                    zIndex: 1300,
                    position: 'relative',
                    width: '100%'
                  }}
                >
                  <ButtonGroup
                    variant="contained"
                    color="primary"
                    style={{
                      borderRadius: '8px',
                      boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                      overflow: 'visible',
                      position: 'relative',
                      zIndex: 1,
                      width: '100%'
                    }}
                  >
                    <Button
                      onClick={() => handleOpenCampaignModal('UNICA')}
                      style={{
                        textTransform: 'uppercase',
                        fontWeight: '600',
                        fontSize: '14px',
                        padding: '10px 20px',
                        letterSpacing: '0.5px',
                        flex: 1
                      }}
                      startIcon={<AddIcon />}
                    >
                      {i18n.t("campaigns.buttons.add")}
                    </Button>
                    <Button
                      size="small"
                      onClick={handleOpenAddMenu}
                      style={{
                        minWidth: '36px',
                        padding: '0 8px',
                        borderLeft: '1px solid rgba(255, 255, 255, 0.2)'
                      }}
                    >
                      <ArrowDropDownIcon />
                    </Button>
                  </ButtonGroup>
                  <Menu
                    anchorEl={addMenuAnchorEl}
                    open={Boolean(addMenuAnchorEl)}
                    onClose={handleCloseAddMenu}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    style={{
                      zIndex: 1301
                    }}
                    PaperProps={{
                      style: {
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        minWidth: '250px',
                        marginTop: '4px',
                        zIndex: 1301
                      }
                    }}
                    MenuListProps={{
                      style: {
                        padding: '4px 0'
                      }
                    }}
                    getContentAnchorEl={null}
                  >
                    <MenuItem
                      onClick={() => {
                        handleOpenCampaignModal('UNICA');
                        handleCloseAddMenu();
                      }}
                      style={{
                        padding: '12px 20px',
                        fontSize: '14px'
                      }}
                    >
                      <Box display="flex" alignItems="center" width="100%">
                        <span role="img" aria-label="Única" style={{ marginRight: 12, fontSize: '20px' }}>📱</span>
                        Campaña ÚNICA
                      </Box>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleOpenCampaignModal('RECURRENTE');
                        handleCloseAddMenu();
                      }}
                      style={{
                        padding: '12px 20px',
                        fontSize: '14px'
                      }}
                    >
                      <Box display="flex" alignItems="center" width="100%">
                        <span role="img" aria-label="Recurrente" style={{ marginRight: 12, fontSize: '20px' }}>🔄</span>
                        Campaña RECURRENTE
                      </Box>
                    </MenuItem>
                  </Menu>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MainHeader>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
      >
        <Table size="small" style={{ minWidth: "1520px" }}>
          <TableHead>
            <TableRow>
              <TableCell align="center" className={classes.tableHeader}>
                {i18n.t("campaigns.table.name")}
              </TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                {i18n.t("campaigns.table.status")}
              </TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                Tipo
              </TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                Ejecuciones
              </TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                Próxima Ejecución
              </TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                {i18n.t("campaigns.table.contactList")}
              </TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                {i18n.t("campaigns.table.whatsapp")}
              </TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                {i18n.t("campaigns.table.aiAgent")}
              </TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                {i18n.t("campaigns.table.scheduledAt")}
              </TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                {i18n.t("campaigns.table.completedAt")}
              </TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                {i18n.t("campaigns.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(() => {
              // ✅ Calcular campañas paginadas
              const paginatedCampaigns = campaigns.slice(
                page * rowsPerPage,
                page * rowsPerPage + rowsPerPage
              );

              return paginatedCampaigns.map((campaign) => (
                <CampaignTableRow
                  key={campaign.id}
                  campaign={campaign}
                  classes={classes}
                  formatDateTime={formatDateTime}
                  formatScheduledDate={formatCampaignScheduledDate}
                  handleEditCampaign={handleEditCampaign}
                  handleDeleteCampaign={handleDeleteCampaignClick}
                  handlePauseCampaign={handlePauseCampaign}
                  handleStopCampaign={handleStopCampaign}
                  restartCampaign={restartCampaign}
                  handleCopyCampaign={handleCopyCampaign}
                  setExecutionsModalOpen={setExecutionsModalOpen}
                  setSelectedCampaignForExecutions={setSelectedCampaignForExecutions}
                />
              ));
            })()}
            {loading && <TableRowSkeleton columns={11} />}
          </TableBody>
        </Table>

        {/* ✅ PAGINACIÓN: Solo aparece cuando hay resultados */}
        {!loading && campaigns.length > 0 && (
          <TablePagination
            component="div"
            count={campaigns.length}
            page={page}
            onPageChange={(event, newPage) => {
              setPage(newPage);
            }}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 13, 20, 50, 100]}
            labelRowsPerPage="Registros por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            style={{
              backgroundColor: 'transparent',
              color: 'inherit',
            }}
          />
        )}
      </Paper>

      {/* Modal de Ejecuciones */}
      {selectedCampaignForExecutions && (
        <CampaignExecutionsModal
          open={executionsModalOpen}
          onClose={() => {
            setExecutionsModalOpen(false);
            setSelectedCampaignForExecutions(null);
          }}
          campaignId={selectedCampaignForExecutions.id}
          campaignName={selectedCampaignForExecutions.name}
        />
      )}
    </MainContainer>
  );
};

export default Campaigns;
